import { tuning } from '@/config/tuning';

/**
 * physics/goalNet.ts — THE FINISH GOAL'S NET (M157). PURE, unit-tested.
 *
 * The goal stands on the finish line across the whole road; behind it the
 * net is a damped spring. Anything pushing past the net's rest surface is
 * pulled back in proportion to how far it has stretched it and slowed in
 * proportion to its speed, so a ball smashed in at race pace bulges the net,
 * stops, and drops back a short way — it settles INSIDE the goal instead of
 * bouncing back over the line. Cars get the same catch. The mesh also grabs
 * motion along it (a ball doesn't skate sideways down the back of a net).
 *
 * Everything here is a pure function of the body's position and velocity in
 * GOAL coordinates — no hidden state — so online clients predict it exactly
 * from any snapshot. The side nets are the guardrails (the road's lateral
 * clamp already does that job); this file owns the back net, the roof net
 * and the crossbar.
 *
 * Coordinates: `s` = distance past the goal line along the goal's forward
 * axis (positive = inside the goal), `h` = height above the goal line's road,
 * `lat` = across the mouth (positive = right).
 */

export type GoalCfg = typeof tuning.goal;

/** The goal, placed: the goal-line centre on the road + its axes. Built once
 *  per track (Track.goalFrame) from the finish sample. */
export interface GoalFrame {
  x: number;
  y: number;
  z: number;
  /** Unit forward — INTO the goal. */
  fx: number;
  fz: number;
  /** Unit right — across the mouth. */
  rx: number;
  rz: number;
  /** Half the mouth's width (the road half-width: post to post). */
  halfWidth: number;
}

/** A body in goal coordinates. */
export interface GoalCoords {
  s: number;
  lat: number;
}

export function toGoalCoords(frame: GoalFrame, x: number, z: number): GoalCoords {
  const dx = x - frame.x;
  const dz = z - frame.z;
  return { s: dx * frame.fx + dz * frame.fz, lat: dx * frame.rx + dz * frame.rz };
}

/** How far past the posts, sideways, the goal still acts. The goal is a
 *  straight box but the road may keep curving through it (measured: up to
 *  ~2 u across the 9 tour courses at the board, more on a random quick-race
 *  course), and the rails — not the goal — clamp a body sideways, so a body
 *  hugging the outside rail deep in the net is still in the goal. Nothing
 *  else of the course can come this close (headings are clamped: no loops). */
export const GOAL_SIDE_SLACK = 6;

/** Is this point anywhere the goal's nets could touch (inside the mouth, from
 *  just in front of the line to the net's furthest stretch)? Cheap reject. */
export function nearGoal(frame: GoalFrame, c: GoalCoords, reach: number, cfg: GoalCfg = tuning.goal): boolean {
  return (
    c.s > -reach - cfg.postRadius * 2 &&
    c.s < cfg.depth + cfg.maxStretch + reach &&
    Math.abs(c.lat) < frame.halfWidth + GOAL_SIDE_SLACK + reach
  );
}

/** The back net's rest depth at a height: it slants from `depth` at the turf
 *  to `depthTop` under the roof (a real goal's net hangs that way). */
export function netDepthAt(h: number, cfg: GoalCfg = tuning.goal): number {
  const t = Math.min(1, Math.max(0, h / cfg.height));
  return cfg.depth + (cfg.depthTop - cfg.depth) * t;
}

/** The result of the back net acting on one body for one step. */
export interface NetCatch {
  /** New distance past the goal line (only changes at the hard stretch cap). */
  s: number;
  /** New speed along the goal's forward axis (positive = deeper in). */
  vAlong: number;
  /** True while the body is stretching the net (the grip applies). */
  contact: boolean;
}

/**
 * The back net on one body: `s` its centre's distance past the goal line,
 * `reach` how far its surface sticks out toward the net (a ball's radius, a
 * car's projected half-extent), `rest` the net's rest depth at the body's
 * height, `vAlong` its speed into the goal. Returns null when it isn't
 * touching the net. Semi-implicit spring-damper, then the hard cap.
 */
export function catchInNet(
  s: number,
  reach: number,
  rest: number,
  vAlong: number,
  dt: number,
  cfg: GoalCfg = tuning.goal,
): NetCatch | null {
  const stretch = s + reach - rest;
  if (stretch <= 0) return null;
  let v = vAlong - (cfg.stiffness * stretch + cfg.damping * vAlong) * dt;
  let ns = s;
  if (stretch > cfg.maxStretch) {
    // the net is at full give: nothing goes further, whatever its speed
    ns = rest + cfg.maxStretch - reach;
    if (v > 0) v = 0;
  }
  return { s: ns, vAlong: v, contact: true };
}

/** Grip: the share of along-the-net motion kept this step while in contact. */
export function netGripKeep(dt: number, cfg: GoalCfg = tuning.goal): number {
  return Math.max(0, 1 - cfg.grip * dt);
}

/** The crossbar's centre height above the goal line's road. */
export function crossbarCentreH(cfg: GoalCfg = tuning.goal): number {
  return cfg.height + cfg.postRadius;
}

/** The result of the crossbar acting on a ball. */
export interface BarHit {
  /** Push to apply in (along, up). */
  ds: number;
  dh: number;
  /** New velocity in (along, up). */
  vs: number;
  vh: number;
}

/**
 * The crossbar on a ball: a round bar across the mouth on the goal line. In
 * the (s, h) plane it is a circle; a ball overlapping it is pushed out along
 * the line between the centres and rings off with crossbarRestitution. (At
 * this game's ball heights it is close to unreachable — it exists so the
 * goal is solid, not because it decides races.)
 */
export function crossbarHit(
  s: number,
  h: number,
  radius: number,
  vs: number,
  vh: number,
  cfg: GoalCfg = tuning.goal,
): BarHit | null {
  const dsC = s;
  const dhC = h - crossbarCentreH(cfg);
  const d = Math.hypot(dsC, dhC);
  const min = radius + cfg.postRadius;
  if (d >= min) return null;
  // dead-centre overlap (never in practice): push it back out of the goal
  const ns = d > 1e-6 ? dsC / d : -1;
  const nh = d > 1e-6 ? dhC / d : 0;
  const over = min - d;
  const into = -(vs * ns + vh * nh);
  let nvs = vs;
  let nvh = vh;
  if (into > 0) {
    const k = 1 + cfg.crossbarRestitution;
    nvs += k * into * ns;
    nvh += k * into * nh;
  }
  return { ds: ns * over, dh: nh * over, vs: nvs, vh: nvh };
}

/**
 * The roof net on a ball inside the goal: a ball rising into it is held under
 * it (its upward speed is soaked up, so it drops). Only acts on a ball that is
 * IN the goal (behind the line, under the roof's far edge) and touching the
 * roof from below. Returns the new (h, vh) or null.
 */
export function roofHold(
  s: number,
  h: number,
  radius: number,
  vh: number,
  cfg: GoalCfg = tuning.goal,
): { h: number; vh: number } | null {
  if (s <= cfg.postRadius) return null; // the crossbar owns the line itself
  if (s > cfg.depthTop + cfg.maxStretch) return null;
  const top = cfg.height - radius;
  if (h <= top || h > cfg.height + radius) return null;
  return { h: top, vh: vh > 0 ? -vh * 0.15 : vh };
}

/** How far a car's body reaches toward the back net: its box projected on
 *  the goal's forward axis (nose-first = half its length; side-on = half its
 *  width). `cosA` = cos of the angle between its heading and the goal axis. */
export function carReach(cosA: number, halfLength: number, halfWidth: number): number {
  const c = Math.abs(cosA);
  const sn = Math.sqrt(Math.max(0, 1 - c * c));
  return c * halfLength + sn * halfWidth;
}
